import { useMessageWithProcessSpecification } from 'services/repositories/messages/messages';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useInfiniteRelationships } from 'services/repositories/relationships/relationships';
import { DocumentType, PartnerDTO, RelationshipV2, ValueOf } from 'support/types';

export const getTradePartner = (relationship: RelationshipV2, currentPartnerId: string) => {
  return relationship.sender_partner?.id === currentPartnerId
    ? relationship.receiver_partner
    : relationship.sender_partner;
};

type UseDocumentCreationRelationshipProps = {
  sourceMessageId: string | undefined;
  documentType: DocumentType | ValueOf<DocumentType> | undefined;
};

type UseDocumentCreationRelationshipResult = RelationshipV2 | undefined;

export const useDocumentCreationRelationship = ({
  sourceMessageId,
  documentType,
}: UseDocumentCreationRelationshipProps): UseDocumentCreationRelationshipResult => {
  const { data: currentPartner } = useCurrentPartner();
  const { data: sourceMessage } = useMessageWithProcessSpecification({
    variables: { messageId: sourceMessageId },
    staleTime: Infinity,
    enabled: !!sourceMessageId,
  });

  const otherPartner: PartnerDTO | undefined =
    currentPartner && sourceMessage?.data && sourceMessage.data.sender_partner?.id === currentPartner?.id
      ? sourceMessage.data.receiver_partner || undefined
      : sourceMessage?.data.sender_partner || undefined;

  // Load relationship using partner id and message types
  const { data: relationships } = useInfiniteRelationships({
    variables: {
      query: {
        perPage: '100',
        filterValues: otherPartner?.id
          ? [
              {
                key: 'partner_id',
                value: otherPartner?.id,
              },
            ]
          : [],
      },
    },
    enabled: Boolean(otherPartner?.id),
  });

  return relationships?.pages
    .flatMap((page) => page.data)
    .find((relationship) => {
      return relationship?.sender_partner?.id === currentPartner?.id && relationship.message_type === documentType;
    });
};

export const isDocumentReceiver = (
  relationships: Array<RelationshipV2>,
  documentType: DocumentType | ValueOf<DocumentType>,
  partnerId: string,
) => {
  return relationships.some((rel) => rel.message_type === documentType && rel.receiver_partner?.id === partnerId);
};

export const isDocumentSender = (
  relationships: Array<RelationshipV2>,
  documentType: DocumentType | ValueOf<DocumentType>,
  partnerId: string,
) => {
  return relationships.some((rel) => rel.message_type === documentType && rel.sender_partner?.id === partnerId);
};

export const isSupplierInRelationship = (relationships: Array<RelationshipV2>, partnerId: string) => {
  return (
    isDocumentReceiver(relationships, DocumentType.order, partnerId) ||
    isDocumentSender(relationships, DocumentType.shippingNotice, partnerId)
  );
};

export const isBuyerInRelationship = (relationships: Array<RelationshipV2>, partnerId: string) => {
  return (
    isDocumentSender(relationships, DocumentType.order, partnerId) ||
    isDocumentReceiver(relationships, DocumentType.shippingNotice, partnerId)
  );
};

export const isSupplierAccordingToDocumentTypes = (
  sentDocumentTypes: Array<DocumentType>,
  receivedDocumentTypes: Array<DocumentType>,
) => receivedDocumentTypes.includes(DocumentType.order) || sentDocumentTypes.includes(DocumentType.shippingNotice);

export const isBuyerAccordingToDocumentTypes = (
  sentDocumentTypes: Array<DocumentType>,
  receivedDocumentTypes: Array<DocumentType>,
) => sentDocumentTypes.includes(DocumentType.order) || receivedDocumentTypes.includes(DocumentType.shippingNotice);

export type RelationshipVariables = {
  partnerName: string;
  messageType: RelationshipV2['message_type'];
  direction: 'OUTBOUND' | 'INBOUND';
};

export const extractRelationshipVariables = (
  relationship: RelationshipV2,
  currentPartnerId: string,
): RelationshipVariables => {
  const tradePartner = getTradePartner(relationship, currentPartnerId);
  const direction = relationship.sender_partner?.id === currentPartnerId ? 'OUTBOUND' : 'INBOUND';

  return {
    partnerName: tradePartner?.name ?? '',
    messageType: relationship.message_type,
    direction,
  };
};

export const isCurrentPartnerTheMessageReceiver = (
  message: { receiver_partner?: { id: string } | null } | undefined,
  currentPartnerId: string | undefined,
): boolean => {
  if (!currentPartnerId || !message?.receiver_partner?.id) {
    return false;
  }
  return message.receiver_partner.id === currentPartnerId;
};
